import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../../components/Icon/Icon';
import CreationCard from '../../components/CreationCard/CreationCard';
import FilterBar from '../../components/FilterBar/FilterBar';
import { searchCreations, resetSearchState, setSearchOverlayQuery, setSearchActiveTab, setActiveTab } from '../../Reducers/actions/creationsActions';
import { searchUsers } from '../../Reducers/actions/userActions';
import './Search.scss';
import { searchNews, setSearchOverlayQueryNews } from '../../Reducers/actions/newsActions';
import NewsItem from '../../components/NewsItem/NewsItem';
import FilterBarNews from '../../components/FilterBarNews/FilterBarNews';
import CreationSkeleton from '../../components/CreationCard/CreationSkeleton';
import NewsItemSkeleton from '../../components/NewsItem/NewsItemSkeleton';
import { getProfilePublicPath } from '../../../helpers/profilePaths';
import { supabase } from '../../../supabase';

const Search = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type: routeType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') ?? '';
  const isAuthenticated = useSelector((state) =>
    state.auth?.auth?.isLogged || state.auth?.user || state.auth?.session
  );
  // Redux auth hydrates asynchronously, so read the stored session directly to avoid
  // treating a logged-in user as logged out on a fresh page load
  const [hasStoredSession, setHasStoredSession] = useState(null);
  useEffect(() => {
    let active = true;
    supabase.auth.getSession()
      .then(({ data }) => { if (active) setHasStoredSession(!!data?.session); })
      .catch(() => { if (active) setHasStoredSession(false); });
    return () => { active = false; };
  }, []);
  const isLoggedOut = !isAuthenticated && hasStoredSession === false;
  // News and library search need an account; logged-out visitors get the public creations/people search
  const isAccountOnlyRoute = routeType === 'news' || routeType === 'library';
  const type = isLoggedOut && isAccountOnlyRoute ? undefined : routeType;
  // Until we know whether there's a session, we can't tell which search a news/library URL should run
  const isSearchTypePending = isAccountOnlyRoute && !isAuthenticated && hasStoredSession === null;

  const {
    searchOverlayQuery,
    searchResults,
    searchLoading,
    searchError,
    searchHasRun,
    searchActiveTab,
    searchPeopleResults,
    searchPeopleLoading,
    searchPeopleError,
    searchPeopleHasRun,
    activeTab // For FilterBar category filtering
  } = useSelector(state => ({
    searchOverlayQuery: type === 'news' ? state.news.searchOverlayQuery : state.creation.searchOverlayQuery,
    searchResults: type === 'news' ? state.news.searchResults : state.creation.searchResults,
    searchLoading: type === 'news' ? state.news.searchLoading : state.creation.searchLoading,
    searchError: type === 'news' ? state.news.searchError : state.creation.searchError,
    searchHasRun: type === 'news' ? state.news.searchHasRun : state.creation.searchHasRun,
    searchActiveTab: state.creation.searchActiveTab,
    searchPeopleResults: state.creation.searchPeopleResults,
    searchPeopleLoading: state.creation.searchPeopleLoading,
    searchPeopleError: state.creation.searchPeopleError,
    searchPeopleHasRun: state.creation.searchPeopleHasRun,
    activeTab: state.creation.activeTab
  }));
  const [searchQuery, setSearchQuery] = useState(urlQuery || searchOverlayQuery || '');
  const timeoutRef = useRef(null);
  // Last ?q= value already reflected in the input, so our own URL writes don't re-trigger a search
  const syncedUrlQueryRef = useRef(null);

  // Filter search results by category (same logic as FeedTab)
  const filteredSearchResults = useMemo(() => {
    if (!searchResults || !Array.isArray(searchResults)) return [];

    // Apply category filter like in FeedTab
    if (activeTab === 'all' || !activeTab) return searchResults;
    return searchResults.filter(creation =>
      creation.category?.toLowerCase() === activeTab.toLowerCase()
    );
  }, [searchResults, activeTab]);


  // Create a debounced version of the search function
  const debouncedSearch = useCallback((query) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        if (searchActiveTab === 'creations') {
          dispatch(setActiveTab('all')); // Reset filter to show all results
          if (type === 'news') {
            dispatch(setSearchOverlayQueryNews(query));
          } else {
            dispatch(setSearchOverlayQuery(query));
          }
          type === "news" ? dispatch(searchNews(query)) : type === 'library' ? dispatch(searchCreations(query, true)) : dispatch(searchCreations(query));
        } else if (searchActiveTab === 'people') {
          dispatch(searchUsers(query));
        }
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 300);
  }, [dispatch, searchActiveTab, type]);

  // Run a search right away (no debounce) for the active tab
  const runSearchNow = useCallback((query) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (searchActiveTab === 'people') {
      dispatch(searchUsers(query));
      return;
    }

    dispatch(setActiveTab('all')); // Reset filter to show all results
    if (type === 'news') {
      dispatch(setSearchOverlayQueryNews(query));
      dispatch(searchNews(query));
    } else {
      dispatch(setSearchOverlayQuery(query));
      dispatch(searchCreations(query, type === 'library'));
    }
  }, [dispatch, searchActiveTab, type]);

  // Mirror the query into ?q= (replace, so typing doesn't flood history)
  const syncQueryToUrl = useCallback((query) => {
    syncedUrlQueryRef.current = query;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (query) {
        next.set('q', query);
      } else {
        next.delete('q');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Pick up ?q= on load (e.g. from the header search) and on back/forward.
  // Layout effect so stale results from a previous query never paint under the new input.
  useLayoutEffect(() => {
    // Wait for the session check so a logged-out deep link never hits the account-only search
    if (isSearchTypePending) return;
    const isFirstSync = syncedUrlQueryRef.current === null;
    if (urlQuery === syncedUrlQueryRef.current) return;
    syncedUrlQueryRef.current = urlQuery;

    // Plain /search keeps whatever query is already in the store
    if (isFirstSync && !urlQuery) return;

    // Returning to a search whose results are still in the store: don't refetch
    const activeTabHasRun = searchActiveTab === 'people' ? searchPeopleHasRun : searchHasRun;
    if (isFirstSync && urlQuery === searchOverlayQuery && activeTabHasRun) return;

    setSearchQuery(urlQuery);
    dispatch(resetSearchState());
    // Record the query for every tab/slice (resetSearchState doesn't touch the news slice,
    // and the people search doesn't store it), so a return visit can skip the refetch
    if (type === 'news') {
      dispatch(setSearchOverlayQueryNews(urlQuery));
    } else {
      dispatch(setSearchOverlayQuery(urlQuery));
    }
    if (urlQuery.trim().length >= 2) {
      runSearchNow(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, isSearchTypePending]);

  // Reset activeTab to 'all' when component mounts or when switching to creations tab
  useEffect(() => {
    if (searchActiveTab === 'creations') {
      dispatch(setActiveTab('all'));
    }
  }, [dispatch, searchActiveTab]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    syncQueryToUrl(query);
    if (type === 'news') {
      dispatch(setSearchOverlayQueryNews(query));
    } else {
      dispatch(setSearchOverlayQuery(query));
    }

    if (query.length >= 2) {
      debouncedSearch(query);
    } else {
      dispatch(resetSearchState());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      runSearchNow(searchQuery);
    }
  };

  const getPlaceholderText = () => {
    switch (searchActiveTab) {
      case 'people':
        return 'Search for people...';
      case 'creations':
      default:
        return 'Search...';
    }
  };

  const handleTabSwitch = async (tab) => {
    dispatch(setSearchActiveTab(tab));

    // Auto-trigger search if there's a query and the target tab hasn't been searched
    const hasValidQuery = searchQuery.trim() && searchQuery.trim().length >= 2;

    if (hasValidQuery) {
      // Check if we need to search the target tab
      const needsCreationsSearch = tab === 'creations' && !searchHasRun;
      const needsPeopleSearch = tab === 'people' && !searchPeopleHasRun;

      if (needsCreationsSearch) {
        // Clear any pending timeout and search immediately for creations
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        dispatch(setActiveTab('all')); // Reset filter to show all results
        if (type === 'news') {
          dispatch(setSearchOverlayQueryNews(searchQuery));
          await dispatch(searchNews(searchQuery));
        } else {
          dispatch(setSearchOverlayQuery(searchQuery));
          await dispatch(searchCreations(searchQuery));
        }
      } else if (needsPeopleSearch) {
        // Clear any pending timeout and search immediately for people
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        await dispatch(searchUsers(searchQuery));
      }
    }

    // Don't reset search results - keep them persistent
  };

  const renderSearchResult = (result) => {
    return (
      type === 'news' ?
        <NewsItem key={result.id} news={result} />
        :
        <CreationCard
          key={result.creation_id}
          thumbnailUrl={result.thumbnail_url}
          category={result.category}
          title={result.title}
          price={result.price}
          tags={result.tags}
          description={result.description}
          users={result.users}
          onClick={() => navigate(`/creations/${result.creation_id}`)}
          media={result.media}
        />
    );
  };

  const renderPeopleResult = (result) => {
    // Determine the image source once to prevent flickering
    const getImageSource = () => {
      if (result.profile_picture && result.profile_picture.trim() !== '') {
        return result.profile_picture;
      }
      if (result.avatar_url && result.avatar_url.trim() !== '') {
        return result.avatar_url;
      }
      return '/avatar.png'; // Use existing avatar from public folder
    };

    const imageSrc = getImageSource();
    const defaultAvatarUrl = window.location.origin + '/avatar.png';

    return (
      <div
        key={result.user_id}
        className="search-page__people-result"
        onClick={() => {
          const p = getProfilePublicPath({
            user_id: result.user_id,
            username: result.username,
            userName: result.userName,
          });
          if (p) navigate(p);
        }}
      >
        <div className="search-page__people-avatar">
          <img
            src={imageSrc}
            alt={result.full_name || result.username || 'User'}
            onError={(e) => {
              // Only set to default if it's not already the default to prevent loops
              if (e.target.src !== defaultAvatarUrl) {
                e.target.src = '/avatar.png';
              }
            }}
            loading="lazy"
          />
        </div>
        <div className="search-page__people-info">
          <div className="search-page__people-username">{result.username}</div>
          <div className="search-page__people-name">{result.full_name || result.username}</div>
        </div>
      </div>
    );
  };

  const handleBack = async () => {
    dispatch(resetSearchState());
    navigate(-1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    syncQueryToUrl('');
    dispatch(resetSearchState());
    if (type === 'news') {
      dispatch(setSearchOverlayQueryNews(''));
    } else {
      dispatch(setSearchOverlayQuery(''));
    }
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return (
    <>
      {/* Dark overlay behind search - shown while typing, hidden on Enter */}

      <div className={`search-page ${searchActiveTab === 'creations' && searchHasRun && searchResults?.length > 0 ? 'search-page--with-filter' : ''}`}>
        <div className="search-page__header">
          <button className="search-page__back" onClick={handleBack}>
            <Icon alias="chevron-left" width={24} color="$dark" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </button>
          <div className="search-page__search-container">
            <input
              type="text"
              className="search-page__search-input"
              placeholder={getPlaceholderText()}
              value={searchQuery}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <Icon alias="search_input_icon" width={20} stroke="#FF3232" color='none' stroke-linecap="round" stroke-linejoin="round" strokeWidth={2} className="search-page__search-icon" />

            {searchQuery && (
              <button
                className="search-page__clear-button"
                onClick={handleClearSearch}
                aria-label="Clear search query"
              >
                <Icon alias="cross" width={12} color="$grey7" />

              </button>
            )}

          </div>

        </div>

        {/* Search Tabs */}
        <div className="search-page__tabs_search">
          <button
            className={`search-page__tab ${searchActiveTab === 'people' ? 'search-page__tab--active' : ''}`}
            onClick={() => handleTabSwitch('people')}
          >
            PEOPLE
          </button>
          <button
            className={`search-page__tab ${searchActiveTab === 'creations' ? 'search-page__tab--active' : ''}`}
            onClick={() => handleTabSwitch('creations')}
          >
            {type === 'news' ? "NEWS" : "CREATIONS"}
          </button>
        </div>

        {/* Filter Bar - Only show for CREATIONS tab when there are results */}


        <div className={type === 'news' ? 'search-page__content-news' : `search-page__content`}>
          {searchActiveTab === 'creations' && searchHasRun && searchResults?.length > 0 && (
            type === 'news' ? <FilterBarNews /> : <FilterBar />
          )}
          {searchActiveTab === 'creations' ? (
            searchLoading ? (
              <div>
                {type === 'news' ? <FilterBarNews /> : <FilterBar />}
                {[1, 2, 3, 4].map((creation) => type !== 'news' ? <div className='mt-32 p-16'><CreationSkeleton key={creation} /></div> : <NewsItemSkeleton key={creation} />)}
              </div>

            ) : searchError ? (
              <div className="search-page__no-results">
                <p>Nothing to see here</p>
                <span>Unable to search creations right now</span>
              </div>
            ) : searchHasRun && filteredSearchResults?.length > 0 ? (
              <div className={type === 'news' ? "search-page__news-result" : "search-page__results"}>
                {filteredSearchResults.map(result => renderSearchResult(result))}
              </div>
            ) : searchHasRun && searchQuery.length >= 2 ? (
              searchLoading ?
                [1, 2, 3, 4].map((creation) => <div className='mt-32 p-16'><NewsItemSkeleton key={creation} /></div>)
                :
                <div className="search-page__no-results">
                  <p>Nothing to see here</p>
                  <span>
                    {searchResults?.length > 0 && filteredSearchResults?.length === 0
                      ? `No ${activeTab === 'all' ? '' : activeTab} creations match your search`
                      : 'No creations match your search'
                    }
                  </span>
                </div>
            ) : (
              <div className="search-page__empty">
                <p>Start typing to search...</p>
              </div>
            )
          ) : (
            searchPeopleLoading ? (
              <div className="search-page__loading">
                <p>Searching...</p>
              </div>
            ) : searchPeopleError ? (
              <div className="search-page__no-results">
                <p>Nothing to see here</p>
                <span>Unable to search people right now</span>
              </div>
            ) : searchPeopleHasRun && searchPeopleResults?.length > 0 ? (
              <div className="search-page__results">
                {searchPeopleResults.map(result => renderPeopleResult(result))}
              </div>
            ) : searchPeopleHasRun && searchQuery.length >= 2 ? (
              <div className="search-page__no-results">
                <p>Nothing to see here</p>
                <span>No people match your search</span>
              </div>
            ) : (
              <div className="search-page__empty">
                <p>Start typing to search...</p>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default Search; 